import { useMemo, useState } from 'react';
import axios from 'axios';
import { API, getAttribution, track } from '../lib/site';

const INITIAL_FORM = {
  project_type: '', built_up_area: '', custom_area: '', floors: '',
  city: '', district: '', state: 'Karnataka', project_stage: '',
  name: '', phone: '', email: '', consent: false,
};

/**
 * Business logic for the estimator funnel: form state, validation and submission.
 */
export function useEstimatorForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const canContinueLocation = form.city.trim().length > 0;
  const phoneValid = form.phone.replace(/\D/g, '').length >= 10;
  const canSubmit = form.name.trim().length >= 2 && phoneValid && form.consent && !submitting;

  const areaFinal = useMemo(
    () => (form.custom_area.trim() ? `${form.custom_area.trim()} sq.ft` : form.built_up_area),
    [form.custom_area, form.built_up_area]
  );

  const reset = (preselectType = null) => {
    setSubmitted(null);
    setError('');
    if (preselectType) setForm((f) => ({ ...f, project_type: preselectType }));
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    phone: form.phone.trim(),
    email: form.email.trim() || null,
    project_type: form.project_type || 'Other',
    built_up_area: areaFinal || null,
    floors: form.floors || null,
    city: form.city.trim() || null,
    district: form.district.trim() || null,
    state: form.state.trim() || null,
    project_stage: form.project_stage || null,
    consent: form.consent,
    ...getAttribution(),
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload();
      const res = await axios.post(`${API}/leads`, payload);
      track('lead_submitted', { project_type: payload.project_type, lead_id: res.data.lead_id });
      setSubmitted({ ...payload, lead_id: res.data.lead_id });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or reach us on WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  return { form, setForm, set, submitting, submitted, error, canContinueLocation, canSubmit, submit, reset };
}
